"use client";

import React from "react";
import {
  ConsentCategory,
  useCookiePreferences,
  useCookieBanner,
} from "cookiebanner-finally";
import Flare from "@/components/global/ui/Flare";
import { Button } from "@/shadcn/ui/button";
import { Toggle } from "@/components/global/ui/Toggle";

interface CookieSettingsProps {
  onClose: () => void;
}

export function CookieSettings({ onClose }: CookieSettingsProps) {
  const { preferences, updateCategory } = useCookiePreferences();
  const { acceptAll, denyAll, close } = useCookieBanner();

  const handleSave = () => {
    close();
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onClose();
  };

  const handleDenyAll = () => {
    denyAll();
    onClose();
  };

  return (
    <div className="flex fixed inset-0 justify-center items-center p-4 z-[9999]">
      <div className="overflow-y-auto w-full max-w-6xl max-h-[90vh]">
        <div className="bg-gradient-to-b from-[#0B0F13] from-[11.57%] to-[#98D8FF] to-[719.35%] rounded-[1.5rem] overflow-clip border border-white/10">
          <div className="relative p-[4rem] lg:px-[5rem] lg:py-[7rem]">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-[4.5rem] lg:text-[5rem] font-medium font-news text-white">
                Cookie Settings
              </h2>
              <button
                onClick={onClose}
                className="text-2xl leading-none text-gray-400 hover:text-white"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              <div className="pb-4 border-b border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium text-white font-geist">
                    Necessary
                  </h3>
                  <Toggle checked={true} onChange={() => {}} disabled={true} />
                </div>
                <p className="text-[2rem] text-white/60">
                  These cookies are required for the basic functions of the
                  website and cannot be disabled.
                </p>
              </div>

              <div className="pb-4 border-b border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium text-white font-geist">
                    Marketing
                  </h3>
                  <Toggle
                    checked={preferences.marketing}
                    onChange={(checked) =>
                      updateCategory(ConsentCategory.Marketing, checked)
                    }
                  />
                </div>
                <p className="text-[2rem] text-white/60">
                  These cookies are used to show you relevant advertising and
                  measure the effectiveness of our advertising campaigns. This
                  includes HubSpot, Google Tag Manager, Meta Pixel and LinkedIn
                  Analytics.
                </p>
              </div>

              <div className="pb-4 border-b border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium text-white font-geist">
                    Personalization
                  </h3>
                  <Toggle
                    checked={preferences.preferences}
                    onChange={(checked) =>
                      updateCategory(ConsentCategory.Preferences, checked)
                    }
                  />
                </div>
                <p className="text-[2rem] text-white/60">
                  These cookies store your settings and preferences to provide
                  you with a personalized experience.
                </p>
              </div>

              <div className="pb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium text-white font-geist">
                    Analytics
                  </h3>
                  <Toggle
                    checked={preferences.analytics}
                    onChange={(checked) =>
                      updateCategory(ConsentCategory.Analytics, checked)
                    }
                  />
                </div>
                <p className="text-[2rem] text-white/60">
                  These cookies help us understand how visitors interact with
                  our website by collecting and reporting anonymous information.
                </p>
              </div>
            </div>

            <div className="flex flex-col mt-8 sm:flex-row gap-[1rem]">
              <Button onClick={handleSave} variant="default" className="flex-1">
                Save Settings
              </Button>

              <Button
                onClick={handleAcceptAll}
                variant="white"
                className="flex-1"
              >
                Accept All
              </Button>

              <Button
                onClick={handleDenyAll}
                variant="whiteFaded"
                className="flex-1"
              >
                Reject All
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
